import { createColumnHelper } from '@tanstack/react-table';
import { HTMLProps, useMemo } from 'react';
import React from 'react';
import { Translation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { SpinningIcon } from '@/components/common/spinning-icon';

import { IBatch } from '../types/batch';

const columnHelper = createColumnHelper<IBatch>();

function IndeterminateCheckbox({
  indeterminate,
  method,
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = React.useRef<HTMLInputElement>(null!);
  const t: any = useTheme();

  React.useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate]);

  const theme = useMemo(() => t.current, [t.current]);

  return (
    <input
      type="checkbox"
      ref={ref}
      className={`
          h-6
          w-6
          cursor-pointer
          rounded 
          border-gray-300 
          text-${theme}-primary 
          shadow-sm 
          focus:border-${theme}-primary 
          focus:ring 
          focus:ring-offset-0 
          focus:ring-${theme}-light 
          focus:ring-opacity-50
        `}
      onClick={(e) => e.stopPropagation()}
      {...rest}
    />
  );
}

export const measurementColumns = [
  // you can find more details about the select in here:
  // https://tanstack.com/table/v8/docs/examples/react/row-selection
  columnHelper.accessor('id', {
    id: 'select',
    header: ({ table }) => (
      <IndeterminateCheckbox
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }) => (
      <div className="px-1">
        <IndeterminateCheckbox
          {...{
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      </div>
    ),
  }),

  // we only use method to filter out analysis based on the type (wine|yeast).
  // TODO: Remove method from here once backend implement method filter
  columnHelper.accessor('method', {
    id: 'method',
    filterFn: (row, columnId, value) => value.includes(row.getValue(columnId)),
    enableHiding: true,
  }),

  columnHelper.accessor('name', {
    header: () => <Translation>{(t) => <>{t('measurements.name')}</>}</Translation>,
    cell: (props) =>
      props.getValue() ? props.getValue() : props.row.original.id.substring(0, 10),
  }),

  columnHelper.accessor('createdAt', {
    header: () => <Translation>{(t) => <>{t('measurements.startDate')}</>}</Translation>,
    cell: (props) => props.getValue().toLocaleDateString('de-DE'),
  }),

  // viab does not exists in some analysis, therefore we have to replace the value with N/A
  columnHelper.accessor((row) => row.resultStat.viab ?? null, {
    id: 'viab',
    header: () => (
      <>
        <Translation>{(t) => <>{t('measurements.viability')}</>}</Translation>
        <span className="font-normal"> (%)</span>
      </>
    ),
    cell: (props) => {
      return props.getValue() !== null ? (
        <>
          <span className="ml-4">{props.getValue()?.toFixed(2)}</span>
        </>
      ) : null;
    },
  }),

  columnHelper.accessor((row) => row.resultStat.conz ?? null, {
    id: 'conz',
    header: () => (
      <div className="flex flex-col">
        <Translation>{(t) => <>{t('measurements.totalConcentration')}</>}</Translation>
        <Translation>
          {(t) => (
            <span className="font-normal"> ({t('measurements.millionCells')} / ml)</span>
          )}
        </Translation>
      </div>
    ),
    cell: (props) => {
      return typeof props.getValue() !== 'object' ? (
        <>
          <span className="ml-4">{props.getValue()?.toFixed(2)}</span>
        </>
      ) : (
        <div className="flex items-center">
          <div className="px-3 py-1 bg-gray-100 rounded-3xl">
            <SpinningIcon color="fill-wine-primary" />
            <span className="text-xs font-semibold uppercase">In progress</span>
          </div>
        </div>
      );
    },
  }),

  // we only show this for admin users
  columnHelper.accessor('email', {
    id: 'email',
    header: () => <Translation>{(t) => <>{t('Email')}</>}</Translation>,
  }),
];
